// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 관리 화면이 부르는 API
//
//  ★★ [합쳤다 2026-08-11] 이 파일에 **두 번째 fetch 통로**(`req`)가 있었다. 원래 이유는
//     「client.ts 에 타임아웃이 없다」 하나뿐이었는데, 갈라져 있는 동안 값이 하나 더
//     어긋났다 — 이쪽 `req` 는 401 에서 `onUnauthorized()` 를 **안 불렀다.**
//     그 사이 이 통로를 쓰는 화면이 관리 화면 다섯 · `upload/client.ts` ·
//     `ShareVisitor.tsx` 까지 퍼져서, 세션이 끊기면 그 화면들만 로그인으로 못 가고
//     「로그인이 필요합니다」 글자를 띄운 채 멈춰 있었다.
//     → 타임아웃을 `api/client.ts` 로 옮기고 `req`·`AdminError` 도 그쪽에서 낸다.
//       **여기는 재수출만 남긴다.**
//
//  ⚠️ 재수출을 지우고 부르는 쪽 import 를 전부 바꾸고 싶어질 텐데, 지금은 두지 마라.
//     `ShareVisitor.tsx` 와 `upload/client.ts` 가 이 이름을 보고 있고, 그 둘은 이 파일과
//     다른 담당의 몫이다. **통로가 하나면 이름이 어디에 있든 사고는 안 난다.**
// ─────────────────────────────────────────────────────────────────────────

import { req, type Project } from '../../api/client';

export { AdminError, req } from '../../api/client';

/**
 * 이 시간을 넘기면 화면이 「느립니다 — 다시 누르지 마세요」를 띄운다. 침묵이 재시도를 부른다.
 *
 * ★ 요청 상한(`TIMEOUT_MS` 15초)과 달리 이것은 **화면 쪽 상수**라 여기 남는다 —
 *   전송에는 아무 영향이 없고, 「기다리는 사람에게 무엇을 보여줄까」만 정한다.
 *   ⚠️ 15초보다 작아야 뜻이 있다. 크게 잡으면 힌트가 뜨기 전에 요청이 먼저 끊긴다.
 */
export const SLOW_HINT_MS = 3_000;

// ═══ 카탈로그 ═════════════════════════════════════════════════════════════

export interface CatalogModule {
  id: string;
  name: string;
  core: boolean;
  default_enabled: boolean;
  deps: string[];
  sort: number;
}

export interface Catalog {
  modules: CatalogModule[];
  roles: { id: string; name: string; sort: number }[];
  /** DB 의 `reserved_slug` 80개. **프론트에 다시 적지 마라**(slug.ts 머리말). */
  reserved: string[];
  belts: string[];
  slug_pattern: string;
  slug_min: number;
  slug_max: number;
}

export function getCatalog(): Promise<Catalog> {
  return req<Catalog>('GET', '/api/catalog');
}

export interface Org {
  id: string;
  /**
   * ⚠️ **지금은 항상 null 이다.** `app.org` 를 app_api 로 읽으면 42501 이 난다 —
   *   `org_select` 정책이 정책 식 안에서 `app.org_member` 를 참조하는데 그 표의 SELECT 가
   *   app_api 에 없다(둘 다 의도대로 만들어졌는데 서로를 무효로 만든다).
   *   자세한 것은 `apps/api/src/routes/admin.ts` 의 listOrgs 주석. DB 를 고치면 채워진다.
   */
  name: string | null;
  /** ★ **이것 하나가 「+ 현장 만들기」 버튼을 켠다.** perms[] 에는 안 들어 있다(003:102). */
  can_create: boolean;
}

export function getOrgs(): Promise<Org[]> {
  return req<{ orgs: Org[] }>('GET', '/api/orgs').then((r) => r.orgs);
}

export function listProjects(): Promise<Project[]> {
  return req<{ projects: Project[] }>('GET', '/api/projects').then((r) => r.projects);
}

// ═══ 현장 만들기 ══════════════════════════════════════════════════════════

export interface CreateInput {
  org_id: string;
  slug: string;
  name: string;
  belt: string;
  /** `{ upload: true, reality: false … }`. 적지 않은 것은 카탈로그 기본값. */
  modules: Record<string, boolean>;
  managers: string[];
}

/**
 * ★ [017] `role_differs` — **이미 구성원인데 역할이 다르다.**
 *   `already` 와 갈라져 있다. 서버는 `on conflict … do nothing` 이라 이때 **아무 일도
 *   안 일어나는데**, 옛 화면은 「이미 구성원입니다」만 말해서 초대한 사람은 역할이
 *   바뀐 줄 알았다. ⚠️ 서버가 말없이 바꾸지 않는다 — 지금 역할(`role_id`)을 받아
 *   **화면이 사람에게 묻는다.**
 */
export type InviteKind = 'member' | 'already' | 'role_differs' | 'invited' | 'reinvited' | 'mail_failed';

/** 한 주소의 처리 결과. `role_id` 는 `member`·`already`·`role_differs` 일 때만 온다. */
export interface InviteResultRow {
  email: string;
  kind: InviteKind;
  role_id?: string;
}

export interface CreateResult {
  project: { id: string; slug: string; name: string; belt: string; perms: string[] };
  invites: InviteResultRow[];
}

export function createProject(input: CreateInput): Promise<CreateResult> {
  return req<CreateResult>('POST', '/api/projects', input);
}

// ═══ 구성원 ═══════════════════════════════════════════════════════════════

export interface MemberRow {
  user_id: string;
  email: string;
  name: string | null;
  role_id: string;
  granted_at: string;
  is_me: boolean;
}

export interface InviteRow {
  id: string;
  email: string;
  role_id: string;
  expires_at: string;
  created_at: string;
  /** ★ 서버가 `expires_at` 으로 계산해 준 값이다. `state` 로 판정하지 마라 — 만료를
   *  갱신하는 배치가 아직 안 돌아서, state 만 보면 「대기 중」인데 수락은 거부된다. */
  expired: boolean;
  days_left: number;
  /** 보낸 지 며칠 됐나. 3일이 넘으면 화면이 주소 오타를 의심하게 만든다. */
  days_waiting: number;
  /**
   * ★ [017] 초대할 때 적어 둔 **이름 · 소속**. 계정이 아직 없어서 `app_user` 에서 못 읽는다.
   *   ⚠️ 이게 없으면 수락 전까지 목록에 **이메일 주소만** 뜬다 — 「누구를 초대해 뒀더라」를
   *   아무도 모른다. 옛 초대장은 null 이다(칸이 생기기 전에 만들어졌다).
   */
  invitee_name: string | null;
  invitee_company: string | null;
}

export interface MembersView {
  project: { id: string; slug: string; name: string };
  members: MemberRow[];
  invites: InviteRow[];
  /** 화면이 무엇을 그릴지 정하는 신호. **역할 이름으로 판정하지 마라.** */
  can: { invite: boolean; manage: boolean };
}

export function getMembers(slug: string): Promise<MembersView> {
  return req<MembersView>('GET', `/api/projects/${encodeURIComponent(slug)}/members`);
}

/**
 * 사람 추가.
 *
 * ★ [017] `person`(이름 · 소속)은 **주소가 하나일 때만** 서버가 받는다. 명단 열 명에게
 *   같은 이름을 찍으면 그건 거짓말이라, 여럿이면 서버가 조용히 버린다.
 *   이 값이 가는 곳은 `invite.invitee_name/company` 이고, 수락하는 순간
 *   `accept_invite` 가 **비어 있을 때만** `app_user` 로 옮긴다. 그래야 이슈에 회사가 찍힌다.
 */
export function addMembers(
  slug: string,
  emails: string,
  role: 'viewer' | 'project_manager',
  person?: { name?: string; company?: string },
): Promise<{ results: InviteResultRow[] }> {
  return req('POST', `/api/projects/${encodeURIComponent(slug)}/members`, {
    emails,
    role,
    ...(person?.name ? { name: person.name } : {}),
    ...(person?.company ? { company: person.company } : {}),
  });
}

/** 역할 바꾸기. ⚠️ POST 인 이유는 index.ts 의 `Route.method` 가 GET·POST 뿐이라서다. */
export function setMemberRole(
  slug: string,
  userId: string,
  role: 'viewer' | 'project_manager',
): Promise<{ ok: true }> {
  return req('POST', `/api/projects/${encodeURIComponent(slug)}/members/${userId}/role`, { role });
}

/** 내보내기. 확인창을 붙인다 — 초대 취소와 달리 되돌리려면 상대가 다시 수락해야 한다. */
export function removeMember(slug: string, userId: string): Promise<{ ok: true }> {
  return req('POST', `/api/projects/${encodeURIComponent(slug)}/members/${userId}/remove`);
}

/** 초대 취소. 화면 문구는 「초대 취소 (링크 무효화)」 — 무엇이 죽는지 말해야 한다. */
export function revokeInvite(slug: string, inviteId: string): Promise<{ ok: true }> {
  return req('POST', `/api/projects/${encodeURIComponent(slug)}/invites/${inviteId}/revoke`);
}

/**
 * **새 초대장 보내기.** 「재발송」이 아니다 — 서버가 취소 후 새로 만든다.
 * (`create_invite` 를 두 번 부르면 불변 트리거에 걸려 **반드시 실패**한다.)
 */
export function resendInvite(
  slug: string,
  inviteId: string,
): Promise<{ ok: true; results: InviteResultRow[] }> {
  return req('POST', `/api/projects/${encodeURIComponent(slug)}/invites/${inviteId}/resend`);
}

// ═══ 초대 수락 ════════════════════════════════════════════════════════════

/** 본문이 없다 — 토큰은 HttpOnly 쿠키(`cv_inv`)에 있고 JS 는 그걸 못 읽는다. */
export function acceptInvite(): Promise<{ slug: string }> {
  return req<{ slug: string }>('POST', '/api/invites/accept');
}
